import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircleFilled, CloseCircleFilled, LoadingOutlined } from '@ant-design/icons'
import { registration } from '../api'

export type NameState = 'empty' | 'checking' | 'free' | 'taken'

/**
 * Choosing the name the account will be known by.
 *
 * Checked as it is typed, but not on every keystroke: the request waits until
 * typing pauses, and a reply that arrives after the value has moved on is
 * discarded. Without that second part a slow answer for "ba" can land after a
 * fast one for "bat" and mark a free name as taken.
 *
 * The rules are stated before they are broken, because a field that only says
 * what is wrong after the fact makes people guess.
 */
export default function UsernameField({ value, onChange, onState }: {
  value: string
  onChange: (v: string) => void
  onState: (s: NameState) => void
}) {
  const { t } = useTranslation()
  const [state, setState] = useState<NameState>('empty')
  const [reason, setReason] = useState('')
  // Only the newest request may write the result.
  const latest = useRef(0)

  useEffect(() => {
    const v = value.trim()
    if (!v) { setState('empty'); setReason(''); onState('empty'); return }

    setState('checking'); onState('checking')
    const seq = ++latest.current
    const timer = setTimeout(async () => {
      try {
        const r = await registration.checkUsername(v)
        if (seq !== latest.current) return           // a later keystroke won
        const next: NameState = r.available ? 'free' : 'taken'
        setState(next); setReason(r.reason ?? ''); onState(next)
      } catch {
        if (seq !== latest.current) return
        // The server decides at submit anyway; do not block on a network blip.
        setState('free'); setReason(''); onState('free')
      }
    }, 400)
    return () => clearTimeout(timer)
  }, [value, onState])

  const id = 'r-username'
  return (
    <div className={`auth-field name-field${state === 'taken' ? ' has-error' : ''}`}>
      <label htmlFor={id}>{t('username')}</label>
      <div className="input-wrap">
        <input
          id={id} value={value}
          // Folded as it is typed, so what you see is what the ledger stores.
          onChange={e => onChange(e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, ''))}
          autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false}
          inputMode="text" maxLength={32}
          aria-invalid={state === 'taken'} aria-describedby={`${id}-note`}
        />
        <span className="name-state" aria-hidden>
          {state === 'checking' && <LoadingOutlined />}
          {state === 'free' && <CheckCircleFilled className="ok" />}
          {state === 'taken' && <CloseCircleFilled className="bad" />}
        </span>
      </div>
      <span id={`${id}-note`} role="status" aria-live="polite"
        className={state === 'taken' ? 'field-error' : 'field-hint'}>
        {state === 'taken' ? reason
          : state === 'free' ? t('nameFree', { name: value.trim() })
          : t('usernameHint')}
      </span>
    </div>
  )
}
